// Google Ads conversion measurement, in Google Consent Mode v2 "advanced" mode (see lib/consent.ts).
// Privacy rules for the app (health and money records live here):
// - Google's tag loads for everyone, but advertising storage stays denied until the person clicks "Accept". Before
//   that (or after "Reject") Google sets and reads no cookies; it only receives cookieless signals, which it uses
//   to estimate conversions it cannot see directly.
// - No page views are sent, and every hit carries the bare origin instead of the real address, so workspace ids,
//   module names and query strings never reach Google. The only exception is Google's own ad-click identifier
//   (gclid, gbraid or wbraid) when the person arrived from one of our adverts, so the trial can be credited to it.
// - Only two events are sent: a trial started and a subscription bought. No names, emails or record data.
// - Ad personalisation (remarketing) stays denied: health-related audiences are not allowed anyway.
import { readAdsConsent, type AdsConsent } from './consent';

/** Pro Dev Dynamics Google Ads account (445-106-9391). Labels come from the two Seventide conversion actions. */
export const GOOGLE_ADS = {
  id: 'AW-18480144617',
  /** "Seventide trial started" (Sign-up, secondary: used only by the Seventide campaign's own goal). */
  trialStartedLabel: 'n-n6CPjrgJ4dEOnBguxE',
  /** "Seventide subscription" (Subscribe, secondary, value = plan price). */
  subscriptionLabel: 'z109CPvrgJ4dEOnBguxE',
} as const;

type Gtag = (...args: unknown[]) => void;
interface AdsWindow extends Window {
  dataLayer?: unknown[];
  gtag?: Gtag;
}

let loaded = false;

/** Google's ad-click identifiers. The website passes them on in the address (never stored) when someone who
 * clicked one of our adverts follows a link to the app. */
const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;

/** Reads the ad-click identifiers from an address; anything that does not look like one is ignored. */
export function readClickIds(search: string): URLSearchParams {
  const from = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const name of CLICK_ID_PARAMS) {
    const v = from.get(name);
    if (v && /^[\w-]{1,200}$/.test(v)) out.set(name, v);
  }
  return out;
}

// Captured when the app first loads, before the router redirects (e.g. to /sign-in) and drops the query string.
const clickIds = typeof window === 'undefined' ? new URLSearchParams() : readClickIds(window.location.search);

const enabled = () => import.meta.env.MODE !== 'test';

function gtag(..._args: unknown[]): void {
  const w = window as AdsWindow;
  w.dataLayer = w.dataLayer ?? [];
  // gtag.js reads the Arguments object, not an array.
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

/** Loads the Google tag once, with advertising storage granted only if the person has accepted; safe to call repeatedly. */
export function startGoogleAds(consent: AdsConsent | null = readAdsConsent()): void {
  if (!enabled() || loaded) return;
  loaded = true;
  (window as AdsWindow).gtag = gtag;
  const storage = consent === 'granted' ? 'granted' : 'denied';
  gtag('consent', 'default', {
    ad_storage: storage,
    ad_user_data: storage,
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  gtag('js', new Date());
  const ids = clickIds.toString();
  gtag('config', GOOGLE_ADS.id, {
    send_page_view: false,
    page_location: `${window.location.origin}/${ids ? `?${ids}` : ''}`,
    page_referrer: '',
    allow_enhanced_conversions: false,
    url_passthrough: false,
  });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GOOGLE_ADS.id)}`;
  document.head.appendChild(s);
}

/** "Accept" clicked: let the already-loaded tag use advertising storage. */
export function grantGoogleAds(): void {
  if (!loaded) {
    startGoogleAds('granted');
    return;
  }
  gtag('consent', 'update', { ad_storage: 'granted', ad_user_data: 'granted' });
}

/** Consent withdrawn: tell the tag and remove the Google Ads cookies on this site. */
export function stopGoogleAds(): void {
  if (loaded) gtag('consent', 'update', { ad_storage: 'denied', ad_user_data: 'denied' });
  const domains = ['', '; Domain=.seventide.app'];
  for (const name of document.cookie.split(';').map((c) => c.trim().split('=')[0] ?? '')) {
    if (!name.startsWith('_gcl_')) continue;
    for (const d of domains) document.cookie = `${name}=; Path=/; Max-Age=0${d}`;
  }
}

export type AdsConversion =
  | { kind: 'trial_started' }
  | { kind: 'subscription'; valuePence: number; transactionId: string };

/** Sends a conversion if the tag is running. Without consent Google receives it as a cookieless signal. */
export function trackConversion(c: AdsConversion): void {
  if (!loaded) return;
  const label = c.kind === 'trial_started' ? GOOGLE_ADS.trialStartedLabel : GOOGLE_ADS.subscriptionLabel;
  if (!label) return;
  gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS.id}/${label}`,
    ...(c.kind === 'subscription'
      ? { value: c.valuePence / 100, currency: 'GBP', transaction_id: c.transactionId }
      : {}),
  });
}
